import { FC, SyntheticEvent, useEffect, useRef, useState } from 'react';

import { getStaticPrefix } from '@eneco/common/env';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import {
  calloutClass,
  calloutCloseClass,
  calloutMediaClass,
  triggerClass,
  triggerLabelClass,
  widgetClass,
} from '@eneco/custom-components/flows/CallSupport.css';
import { I18nProvider, useTranslation } from '@eneco/i18n';
import { Box, Button, ButtonLink, Grid, IconButton, Image, Stack, Text } from '@eneco/sparky';
import { CloseIcon, PhoneIcon } from '@eneco/sparky/icons';
import { keepFromTracking, useGTM } from '@eneco/tracking';

const PHONE_NUMBER = '088-8955533';
const PHONE_HREF = 'tel:+31888955533';
const CALLOUT_DISMISSED_KEY = 'flow-call-support-callout-dismissed';
const COMPONENT_NAME = 'call-support';

export const CallSupport: FC = () => (
  <I18nProvider dictionary={locale => import(`./content/${locale}.json`)}>
    <CallSupportWidget />
  </I18nProvider>
);

const CallSupportWidget: FC = () => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isCalloutVisible, setCalloutVisible] = useState(false);
  const { pushToDataLayer } = useGTM();
  const { t, isLoading: isTranslationsLoading } = useTranslation();
  const closeTrackedRef = useRef(false);

  useEffect(() => {
    if (sessionStorage.getItem(CALLOUT_DISMISSED_KEY) === null) setCalloutVisible(true);
  }, []);

  const dismissCallout = () => {
    sessionStorage.setItem(CALLOUT_DISMISSED_KEY, 'true');
    setCalloutVisible(false);
  };

  /**
   * Replaces the automatic button/link tracking with an explicit event carrying a stable
   * `component_name`. Without it these interactions are indistinguishable from the dialog's own
   * "Sluiten" button and the flow navigation, which would leave the experiment unmeasurable.
   */
  const track = (event: SyntheticEvent, entry: { event: string; content: string; action: string }) => {
    keepFromTracking(event);
    pushToDataLayer({
      event: entry.event,
      component_name: COMPONENT_NAME,
      component_type: 'widget',
      selected_content: entry.content,
      selected_action: entry.action,
    });
  };

  const closeCallout = (event: SyntheticEvent) => {
    track(event, { event: 'select_button', content: 'callout', action: 'close' });
    dismissCallout();
  };

  const openDialog = (event: SyntheticEvent) => {
    track(event, { event: 'select_button', content: 'bel ons', action: 'open' });
    dismissCallout();
    closeTrackedRef.current = false;
    setDialogOpen(true);
  };

  /**
   * Fires a single widget close event for any way the dialog is dismissed (header icon, the "Sluiten"
   * button, Escape or overlay click). `keepFromTracking` cancels the automatic tracking on the paths
   * that carry a click event; the ref guards against Sparky's duplicate `onClose` invocation.
   */
  const trackDialogClose = (event?: SyntheticEvent) => {
    keepFromTracking(event);
    if (closeTrackedRef.current) return;
    closeTrackedRef.current = true;
    pushToDataLayer({
      event: 'select_button',
      component_name: COMPONENT_NAME,
      component_type: 'widget',
      selected_content: 'sluiten',
      selected_action: 'close',
    });
  };

  const trackPhoneCall = (event: SyntheticEvent) => {
    keepFromTracking(event);
    pushToDataLayer({
      event: 'select_link',
      component_name: COMPONENT_NAME,
      component_type: 'widget',
      selected_content: 'bel eneco',
      selected_action: 'call',
      navigation_goal: PHONE_HREF,
    });
  };

  const imageSource = `${getStaticPrefix()}/images/eneco/eneco-customer-service.png`;

  if (isTranslationsLoading) return null;

  const triggerLabel = t('trigger.label');

  return (
    <>
      <Box className={widgetClass}>
        <Stack direction="column" gap="2" alignX="end">
          {isCalloutVisible ? (
            <Box backgroundColor="backgroundPrimary" borderRadius="s">
              <Box className={calloutClass}>
                {/* overflow="hidden" clips the bleeding artwork to the card's rounded corners. */}
                <Box paddingTop="1">
                  <Stack direction="row">
                    <Stack.Item grow={true}>
                      <Box paddingY="2" paddingLeft="4">
                        <Stack direction="column" gap="1">
                          <Text as="p" size="BodyXL" weight="bold">
                            {t('callout.title')}
                          </Text>
                          <Text as="p" size="BodyS" color="textLowEmphasis">
                            {t('callout.description')}
                          </Text>
                        </Stack>
                      </Box>
                    </Stack.Item>
                    <Stack.Item grow>
                      <Box className={calloutMediaClass}>
                        <Image src={imageSource} alt="" width="108px" />
                      </Box>
                    </Stack.Item>
                  </Stack>
                  <Box className={calloutCloseClass}>
                    <IconButton label={t('callout.close')} onClick={closeCallout} size="large">
                      <CloseIcon size="medium" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : null}

          {/* aria-label repeats the visible label so the icon-only mobile trigger still has an
              accessible name, and so GTM's `getLabel` reports the same label on both breakpoints. */}
          <button type="button" className={triggerClass} aria-label={triggerLabel} onClick={openDialog}>
            <PhoneIcon color="iconElectricityNew" />
            <Text size="BodyM" weight="bold" className={triggerLabelClass}>
              {triggerLabel}
            </Text>
          </button>
        </Stack>
      </Box>

      <TrackedDialog
        isOpen={isDialogOpen}
        setOpen={setDialogOpen}
        title={t('dialog.title')}
        onClose={trackDialogClose}>
        <Stack direction="column" gap="6">
          <Stack direction="row" alignY="center" gap="4">
            <Stack.Item grow={true}>
              <Text as="p" size="BodyM">
                {t('dialog.description')}
              </Text>
            </Stack.Item>
            <Image src={imageSource} alt="" width="120px" />
          </Stack>
          <Grid columns={{ initial: 1, lg: 2 }} gap="2">
            <ButtonLink href={PHONE_HREF} action="primary" onClick={trackPhoneCall}>
              {t('dialog.callButton', { phoneNumber: PHONE_NUMBER })}
            </ButtonLink>
            {/* Radix closes via `onOpenChange`, so the dialog's `onClose` runs without an event and
                cannot cancel the automatic button tracking. Suppress it here on the click itself so
                the single manual close event above is the only one fired. */}
            <TrackedDialog.Close asChild>
              <Button action="secondary" onClick={keepFromTracking}>
                {t('dialog.close')}
              </Button>
            </TrackedDialog.Close>
          </Grid>
        </Stack>
      </TrackedDialog>
    </>
  );
};
